import { useSplitEnabled } from '@buffer-mono/features'
import { PageHeader } from '@buffer-mono/popcorn'
import { Link } from 'react-router-dom'

import { SplitTagBadge } from '~publish/components/SplitTagBadge'
import { useAccount, useIsOwner } from '~publish/contexts/AccountContext'

import { INSIGHTS_HABITS_SPLIT } from '../Habits/habitsSplit'
import { buildHabitsHref } from './insightsSectionHref'
import styles from './InsightsNavigationTabs.module.css'

type InsightsNavigationTabsProps = {
  activeTab: 'post-insights' | 'habits'
  postInsightsHref: string
}

/**
 * Switches between the Insights sections: Post insights and Habits.
 *
 * Habits is alpha-gated and owner-only, so for most viewers there is exactly one
 * section — and a tab bar holding a lone "Post insights" tab reads as a promise
 * of sections that aren't there yet (CORE-7891). The nav therefore renders only
 * when there is somewhere to switch to.
 *
 * It owns its `PageHeader.Row` so that hiding it removes the row's reserved
 * height as well; left to the caller, an emptied row would still draw a blank
 * band under the page title.
 */
export function InsightsNavigationTabs({
  activeTab,
  postInsightsHref,
}: InsightsNavigationTabsProps): JSX.Element | null {
  const { loading } = useAccount()
  const isOwner = useIsOwner()
  const { isEnabled } = useSplitEnabled(INSIGHTS_HABITS_SPLIT)
  const showHabits = !loading && isOwner && isEnabled

  // A viewer already on Habits keeps the nav even when the gate says otherwise
  // — it is their only way back, because the page's access-denied state offers
  // no action of its own.
  if (!showHabits && activeTab !== 'habits') {
    return null
  }

  return (
    <PageHeader.Row>
      <nav aria-label="Insights sections" className={styles.nav}>
        <ul className={styles.list}>
          <li>
            <Link
              to={postInsightsHref}
              className={styles.item}
              aria-current={activeTab === 'post-insights' ? 'page' : undefined}
            >
              Post insights
            </Link>
          </li>
          {showHabits ? (
            <li>
              <Link
                to={buildHabitsHref(postInsightsHref)}
                className={styles.item}
                aria-current={activeTab === 'habits' ? 'page' : undefined}
              >
                Habits
                <SplitTagBadge splitName={INSIGHTS_HABITS_SPLIT} />
              </Link>
            </li>
          ) : null}
        </ul>
      </nav>
    </PageHeader.Row>
  )
}
